import React, { useContext, useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import { format } from "date-fns";
import { AppContext } from "../../AppContextProvider";
import { getRfqStatusSince } from "../../api/ProcurementClient";
import { DateTimeUtils } from "../../DateTimeUtils";
import { getRFQStatusLabel } from "../../models/RFQStatus";

const CACHE_TTL_MS = 60 * 1000;
const sinceCache = new Map<string, { since: string | null; fetchedAt: number }>();

interface RfqStatusSinceTooltipProps {
  rfqId: number;
  status: string;
  children: React.ReactElement;
}

/** Hover tooltip: how long the RFQ has been in its current status. */
const RfqStatusSinceTooltip: React.FC<RfqStatusSinceTooltipProps> = ({
  rfqId,
  status,
  children,
}) => {
  const { rfqStatuses = [] } = useContext(AppContext);
  const cacheKey = `${rfqId}|${status}`;
  const [open, setOpen] = useState(false);
  const [since, setSince] = useState<string | null | undefined>(
    () => sinceCache.get(cacheKey)?.since
  );
  const [failed, setFailed] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    setSince(sinceCache.get(cacheKey)?.since);
    setFailed(false);
  }, [cacheKey]);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    const cached = sinceCache.get(cacheKey);
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      setSince(cached.since);
    } else {
      getRfqStatusSince(rfqId)
        .then((data) => {
          // The row may show an optimistic status the server hasn't recorded yet.
          const value = data.status === status ? data.since : null;
          sinceCache.set(cacheKey, { since: value, fetchedAt: Date.now() });
          if (!cancelled) setSince(value);
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });
    }
    const tick = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => {
      cancelled = true;
      window.clearInterval(tick);
    };
  }, [open, cacheKey, rfqId, status]);

  const label = getRFQStatusLabel(status, rfqStatuses);
  let title: string;
  if (failed) {
    title = `${label} — couldn't load time in status`;
  } else if (since === undefined) {
    title = `${label} — loading…`;
  } else if (since === null) {
    title = `${label} — time in status not tracked yet`;
  } else {
    const seconds = (Date.now() - new Date(since).getTime()) / 1000;
    title = `In ${label} for ${DateTimeUtils.formatDuration(seconds)} (since ${format(
      new Date(since),
      "MM/dd/yy h:mm a"
    )})`;
  }

  return (
    <Tooltip
      title={title}
      enterDelay={300}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      disableInteractive
    >
      {children}
    </Tooltip>
  );
};

export default RfqStatusSinceTooltip;
